import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { type MapMode, mapSquare, squareText } from '../lib/fleet-map';
import type { UtilBox } from '../lib/fleetUtilization';

// One machine on the Machines Map (#6562, prototype `.mt`): a square button whose look,
// glyph and ramp step are `mapSquare`'s, named by `squareText` (name, health, status, CPU),
// so colour is never the only signal: "!" critical, "×" silent, dashed paused, dotted
// starting, and every state in its accessible name. Presenter: `MachineMap` owns which
// square shows its tooltip and what selecting one does.

export function MachineMapSquare(props: {
  readonly box: UtilBox;
  readonly mode: MapMode;
  readonly large: boolean;
  /** The tooltip's id while it describes this square, else undefined. */
  readonly describedBy: string | undefined;
  /** The pointer or focus arrived on this square, or left it (`lib/map-tip.ts`'s claims). */
  readonly onHold: (el: HTMLElement, hold: 'hover' | 'focus') => void;
  readonly onRelease: (el: HTMLElement, hold: 'hover' | 'focus') => void;
  /** Selected: the map opens the machine's details panel. */
  readonly onSelect: (box: UtilBox) => void;
}): JSX.Element {
  const look = () => mapSquare(props.box, props.mode);
  return (
    <li class="machine-map__cell">
      <button
        type="button"
        class="machine-map__square"
        classList={{ 'machine-map__square--lg': props.large }}
        data-look={look().look}
        data-step={look().step ?? undefined}
        aria-label={squareText(props.box, t).name}
        aria-describedby={props.describedBy}
        onPointerEnter={(e) => props.onHold(e.currentTarget, 'hover')}
        onPointerLeave={(e) => props.onRelease(e.currentTarget, 'hover')}
        onFocus={(e) => props.onHold(e.currentTarget, 'focus')}
        onBlur={(e) => props.onRelease(e.currentTarget, 'focus')}
        onClick={() => props.onSelect(props.box)}
      >
        <span aria-hidden="true">{look().glyph}</span>
      </button>
    </li>
  );
}
